import type { ErrorEvent } from "@sentry/nextjs";

/**
 * Shared by the browser, Node and Edge `Sentry.init()` calls. Must stay free of Node/DOM imports.
 *
 * Without `NEXT_PUBLIC_SENTRY_DSN` the SDK is not enabled at all, so local dev and any build that
 * was not given a DSN send nothing.
 */
export const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN || undefined;

export const SENTRY_ENVIRONMENT =
  process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || process.env.NODE_ENV || "development";

function sampleRate(value: string | undefined, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback;
}

export const SENTRY_TRACES_SAMPLE_RATE = sampleRate(
  process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE,
  0.1,
);

/**
 * Headers that carry the session token (`ggps_token` cookie, the mobile app's Bearer header) or
 * the scheduled-report worker's shared secret. Never sent to Sentry.
 */
const SECRET_HEADERS = new Set(["authorization", "cookie", "set-cookie", "x-report-render-secret"]);

const SECRET_KEY = /pass(word)?|token|secret|hash|authorization|cookie/i;

function scrubRecord(record: Record<string, unknown> | undefined) {
  if (!record) return;
  for (const key of Object.keys(record)) {
    if (SECRET_HEADERS.has(key.toLowerCase()) || SECRET_KEY.test(key)) {
      record[key] = "[Filtered]";
    }
  }
}

function scrubQueryString(url: string): string {
  const queryStart = url.indexOf("?");
  if (queryStart < 0) return url;
  const params = new URLSearchParams(url.slice(queryStart + 1));
  let changed = false;
  for (const key of [...params.keys()]) {
    if (SECRET_KEY.test(key)) {
      params.set(key, "[Filtered]");
      changed = true;
    }
  }
  return changed ? `${url.slice(0, queryStart)}?${params.toString()}` : url;
}

/** Strips the session token, cookies and request bodies (the login password) from every event. */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  const request = event.request;
  if (request) {
    delete request.cookies;
    delete request.data;
    scrubRecord(request.headers as Record<string, unknown> | undefined);
    if (typeof request.query_string === "string") {
      request.query_string = scrubQueryString(`?${request.query_string}`).slice(1);
    }
    if (request.url) request.url = scrubQueryString(request.url);
  }
  for (const breadcrumb of event.breadcrumbs ?? []) {
    const data = breadcrumb.data as Record<string, unknown> | undefined;
    if (data && typeof data.url === "string") data.url = scrubQueryString(data.url);
    scrubRecord(data);
  }
  return event;
}

export const sharedSentryOptions = {
  dsn: SENTRY_DSN,
  enabled: Boolean(SENTRY_DSN),
  environment: SENTRY_ENVIRONMENT,
  tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
  // No IP addresses, cookies or request bodies by default; the user is set explicitly (id only).
  sendDefaultPii: false,
  beforeSend: scrubEvent,
} as const;
